import { ADDON_REF_ID_MAPPING, PLAN_STATUS } from "@/data/plans";
import { TUserPlanData } from "@/types/plan-data";
import { derivePlanIntervalType } from "@/utils/derivePlanIntervalType";
import { useMemo } from "react";
import {
  ADDON_CARDS_V1,
  PRICING_CARDS_V1,
  V1_ADDON_DISPLAY_NAME_MAP,
} from "../components/migration/data";
import { TAddOn, TPlan } from "../types.checkout";
import { getAddOnItemList } from "../utils/getAddOnItemList";
import { getLegacyPlanList } from "../utils/getLegacyPlanList";
import { getOwnedPlanList } from "../utils/getOwnedPlanList";

/**
 * Derives user plan state from raw TUserPlanData.
 * Replaces what useCheckout's UserPlan object used to return.
 */
export function useUserPlanDerived(
  userPlan: TUserPlanData | undefined,
  planCards: TPlan[],
  addOnCards: TAddOn[],
) {
  return useMemo(() => {
    const {
      productRefIds = [],
      productRefId,
      purchased_upsells: currentAddOns = [],
      status: planStatus,
      custom_ai_profiles: totalCustomAiProfiles,
      instant_profiles: totalInstantAiProfiles,
      pricing_model_version: userPricingVersion = "v2",
      current_period_end: currentPeriodEnd,
      pause_info: pauseInfo,
      cancel_at: cancelAt,
      plan_price_amount: planPriceAmount,
      isFounder = false,
      recurring,
    } = userPlan ?? {};

    const planIntervalType = derivePlanIntervalType(recurring);

    const isPlanActive = planStatus === PLAN_STATUS.ACTIVE;
    const isPlanPaused = planStatus === PLAN_STATUS.PAUSED;
    const isPlanTrial = planStatus === PLAN_STATUS.TRIALING;
    const isPlanCancelled = planStatus === PLAN_STATUS.CANCELED;
    const isPlanCancelling = !!cancelAt;

    const IS_USER_ON_PRICING_V2 = userPricingVersion === "v2";

    const rawUserProductIds = productRefIds;
    const rawUserProductIdV1 = productRefId;

    const daysUntilPeriodEnd = currentPeriodEnd
      ? Math.ceil(
          (currentPeriodEnd * 1000 - Date.now()) / (1000 * 60 * 60 * 24),
        )
      : null;

    const hasUsedPauseBefore = !!pauseInfo;

    const isPauseDisabled =
      (daysUntilPeriodEnd !== null && daysUntilPeriodEnd < 7) ||
      (hasUsedPauseBefore ?? false);

    const ownedPlanList = getOwnedPlanList(
      planCards,
      isPlanActive || isPlanCancelled || isPlanPaused || isPlanTrial
        ? rawUserProductIds
        : [],
    );

    const paidPlanList = getOwnedPlanList(
      planCards,
      isPlanActive || isPlanPaused ? rawUserProductIds : [],
    );

    const ownedAddOnItemList = getAddOnItemList(currentAddOns, addOnCards);

    const hasGalleryUnlimitedAddOn = ownedAddOnItemList.some(
      (addOn) => addOn.productRefId === ADDON_REF_ID_MAPPING.gallery_unlimited,
    );

    const v1PlanList = rawUserProductIdV1
      ? getLegacyPlanList(PRICING_CARDS_V1, rawUserProductIdV1)
      : [];

    const v1AddOnList = currentAddOns
      .map((userAddOn) => {
        const card = ADDON_CARDS_V1.find((c) => c.id === userAddOn.product_id);
        if (!card) return null;
        return {
          ...card,
          name: V1_ADDON_DISPLAY_NAME_MAP[card.id] ?? card.name,
          quantity: userAddOn.quantity,
          interval: userAddOn.recurring.interval,
        };
      })
      .filter(
        (
          item,
        ): item is (typeof ADDON_CARDS_V1)[number] & {
          quantity: number;
          interval: "month" | "year";
        } => item !== null,
      );

    return {
      rawUserProductIds,
      planStatus,
      isPlanActive,
      isPlanPaused,
      IS_USER_ON_PRICING_V2,
      isPauseDisabled,
      currentPeriodEnd,
      hasUsedPauseBefore,
      daysUntilPeriodEnd,
      isPlanCancelling,
      isPlanCancelled,
      cancelAt,
      resumeAt: pauseInfo?.date_resume,
      ownedPlanList,
      paidPlanList,
      ownedAddOnItemList,
      v1PlanList,
      v1AddOnList,
      totalCustomAiProfiles,
      totalInstantAiProfiles,
      planPriceAmount,
      hasGalleryUnlimitedAddOn,
      isFounder,
    };
  }, [userPlan, planCards, addOnCards]);
}

export type TUserPlanDerived = ReturnType<typeof useUserPlanDerived>;
